import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  ListTodo, 
  MessageSquare, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Mic,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  CalendarClock,
  FolderOpen,
  Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import VoiceRecorderModal from '@/components/VoiceRecorderModal';

const navItems = [
  { path: '/dashboard', label: 'Panel de Control', icon: LayoutDashboard, roles: ['superadmin', 'ugib', 'departamentos', 'direccion'] },
  { path: '/tasks', label: 'Tareas', icon: ListTodo, roles: ['superadmin', 'ugib', 'departamentos', 'direccion'] },
  { path: '/meetings', label: 'Reuniones', icon: CalendarClock, roles: ['superadmin', 'ugib', 'departamentos', 'direccion'] },
  { path: '/queries', label: 'Consultas', icon: MessageSquare, roles: ['superadmin', 'ugib', 'departamentos', 'direccion', 'staff'] },
  { path: '/repositorio', label: 'Repositorio', icon: FolderOpen, roles: ['superadmin', 'ugib'] },
  { path: '/hr-agent', label: 'Agente IA RRHH', icon: Scale, roles: ['superadmin', 'ugib', 'departamentos', 'direccion'] },
  { path: '/agents', label: 'Agentes', icon: Users, roles: ['superadmin', 'ugib', 'departamentos', 'direccion'] },
  { path: '/settings', label: 'Configuracion', icon: Settings, roles: ['superadmin'] },
];

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => hasRole(item.roles));

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar - Desktop */}
      <aside className={cn(
        "hidden lg:flex lg:flex-col h-screen border-r border-slate-800 bg-[#0B1120] text-slate-400 fixed transition-all duration-300",
        collapsed ? "lg:w-16" : "lg:w-64"
      )}>
        <div className={cn("border-b border-slate-800 flex items-center", collapsed ? "p-3 justify-center" : "p-6 justify-between")}>
          {!collapsed && (
            <div>
              <h1 className="text-xl font-semibold text-white tracking-tight font-['Manrope']">
                VITÁCORE
              </h1>
              <p className="text-[10px] text-slate-600 mt-0.5">creado por Joan Monzo</p>
              <p className="text-xs text-slate-500 mt-1">UGOLF ARAVELL</p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-500 hover:text-white transition-colors p-1 rounded"
            data-testid="toggle-sidebar-btn"
            title={collapsed ? 'Expandir menu' : 'Colapsar menu'}
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                title={collapsed ? item.label : undefined}
                className={cn(
                  "flex items-center gap-3 rounded-lg transition-all duration-200",
                  collapsed ? "justify-center px-2 py-3" : "px-4 py-3",
                  isActive
                    ? "bg-slate-800 text-white"
                    : "hover:bg-slate-800/50 hover:text-white"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                {!collapsed && isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Voice Button */}
        {hasRole(['superadmin', 'ugib', 'departamentos', 'direccion']) && (
          <div className={cn("border-t border-slate-800", collapsed ? "p-2" : "p-4")}>
            <Button
              onClick={() => setVoiceModalOpen(true)}
              data-testid="voice-to-task-btn"
              title={collapsed ? 'Voz a Tarea' : undefined}
              className={cn("bg-emerald-700 hover:bg-emerald-800 text-white", collapsed ? "w-full p-2" : "w-full gap-2")}
            >
              <Mic className="w-4 h-4" />
              {!collapsed && 'Voz a Tarea'}
            </Button>
          </div>
        )}

        {/* User Section */}
        <div className={cn("border-t border-slate-800", collapsed ? "p-2" : "p-4")}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                title="Cerrar Sesion"
                className="text-slate-400 hover:text-white hover:bg-slate-800 w-9 h-9"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesion
              </Button>
            </>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className={cn(
        "fixed inset-0 z-50 lg:hidden",
        sidebarOpen ? "block" : "hidden"
      )}>
        <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        <aside className="fixed left-0 top-0 h-full w-64 bg-[#0B1120] text-slate-400 shadow-xl flex flex-col">
          <div className="p-6 border-b border-slate-800 flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-white font-['Manrope']">VITÁCORE</h1>
              <p className="text-[10px] text-slate-600 mt-0.5">creado por Joan Monzo</p>
              <p className="text-xs text-slate-500 mt-1">UGOLF ARAVELL</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-slate-400" />
            </Button>
          </div>
          
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all",
                    isActive ? "bg-slate-800 text-white" : "hover:bg-slate-800/50 hover:text-white"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Voice Button - Mobile */}
          {hasRole(['superadmin', 'ugib', 'departamentos', 'direccion']) && (
            <div className="p-4 border-t border-slate-800">
              <Button
                onClick={() => { setVoiceModalOpen(true); setSidebarOpen(false); }}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
              >
                <Mic className="w-4 h-4" />
                Voz a Tarea
              </Button>
            </div>
          )}

          {/* User Section - Mobile */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { handleLogout(); setSidebarOpen(false); }}
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesion
            </Button>
          </div>
        </aside>
      </div>

      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-300", collapsed ? "lg:ml-16" : "lg:ml-64")}>
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-900">VITÁCORE</h1>
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="min-h-screen">
          {children}
        </main>
      </div>

      {/* Voice Recorder Modal */}
      <VoiceRecorderModal 
        open={voiceModalOpen} 
        onClose={() => setVoiceModalOpen(false)} 
      />
    </div>
  );
};

export default Layout;
